import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';

import { documentApiClient, investorApiClient, paymentsApiClient, userApiClient } from 'api';
import { BalanceDto, InvestorUserAccessDto } from 'api/investor-client';
import { Currency } from 'types';
import { InvestorAccountsContext } from './context';

interface Props {
  loadInitially?: boolean;
}

export interface AccountWithBalance extends InvestorUserAccessDto {
  balance: BalanceDto[];
  unpaid: BalanceDto[];
  VABalance: BalanceDto[];
  pendingPayins: BalanceDto[];
  pendingPayouts: BalanceDto[];
}

const getPrimaryAccount = (accounts: InvestorUserAccessDto[]): InvestorUserAccessDto | null => {
  return accounts.find((acc) => acc.isPrimaryAccount) ?? accounts[0] ?? null;
};

export const InvestorAccountsProvider = ({
                                           loadInitially = true,
                                           children,
                                         }: PropsWithChildren<Props>) => {
  const [selectedAccount, setSelectedAccount] = useState<AccountWithBalance | null>(null);
  const [accounts, setAccounts] = useState<InvestorUserAccessDto[]>([]);
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);

  const updateAccountBalance = useAsyncCallback(async () => {
    const { data } = await investorApiClient.investors.investorApiControllerGetBalance([
      'VirtualAccount:Assets:Current',
      'VirtualAccount:Liabilities:InvestmentPayable',
      'VirtualAccount:Assets:PendingPayin',
      'VirtualAccount:Assets:PendingPayout',
    ]);

    const vaBalanceData = data.filter((e) => e.account === 'VirtualAccount:Assets:Current');
    const unpaidInvestmentsBalanceData = data.filter(
      (e) => e.account === 'VirtualAccount:Liabilities:InvestmentPayable',
    );
    const pendingPayins = data.filter((e) => e.account === 'VirtualAccount:Assets:PendingPayin');
    const pendingPayouts = data.filter((e) => e.account === 'VirtualAccount:Assets:PendingPayout');

    const currencies = Array.from(
      new Set(
        data
          .map((e) => e.currency)
          .filter((c): c is NonNullable<typeof c> => c != null),
      ),
    ) as Currency[];

    const amountFor = (items: BalanceDto[], currency: Currency) =>
      items.find((e) => e.currency === currency)?.amount ?? 0;

    const totalBalance = currencies.map(
      (currency) =>
        ({
          currency,
          amount:
            amountFor(vaBalanceData, currency) +
            amountFor(unpaidInvestmentsBalanceData, currency) +
            amountFor(pendingPayins, currency) +
            amountFor(pendingPayouts, currency),
        }) as BalanceDto,
    );

    setSelectedAccount((state) => {
      if (!state) return null;

      return {
        ...state,
        balance: totalBalance,
        VABalance: vaBalanceData,
        unpaid: unpaidInvestmentsBalanceData,
        pendingPayins,
        pendingPayouts,
      };
    });
  });

  const updateInvestorToken = useAsyncCallback(async () => {
    if (!selectedAccount?.investor?.id) return;

    const { data: accessToken } = await userApiClient.user.userApiControllerGetToken(
      selectedAccount.investor.id,
    );

    investorApiClient.setSecurityData({ token: accessToken });
    paymentsApiClient.setSecurityData({ token: accessToken });
    documentApiClient.setSecurityData({ token: accessToken });

    void updateAccountBalance.execute();
  });

  const fetchAccount = useAsyncCallback(async (withDefaultAccountSelection = true) => {
    const { data } = await userApiClient.user.userApiControllerGetInvestorAccounts();

    setAccounts(data);
    setInitiallyLoaded(true);

    setSelectedAccount((selected) => {
      if (!selected) {
        if (withDefaultAccountSelection) return getPrimaryAccount(data) as AccountWithBalance;
        return null;
      }

      return (data.find((acc) => acc.investor && acc.investor.id === selected.investor?.id) ??
        getPrimaryAccount(data)) as AccountWithBalance;
    });
  });

  const selectAccount = useCallback(
    (accountId?: string) => {
      const acc = accountId
        ? accounts.find((acc) => acc?.investor?.id === accountId)
        : getPrimaryAccount(accounts);

      if (!acc) return false;

      setSelectedAccount({
        ...acc,
        balance: [],
        unpaid: [],
        VABalance: [],
        pendingPayins: [],
        pendingPayouts: [],
      });

      return true;
    },
    [accounts],
  );

  useEffect(() => {
    if (loadInitially) fetchAccount.execute();
  }, []);

  return (
    <InvestorAccountsContext.Provider
      value={{
        accounts,
        selectedAccount,
        selectAccount,
        updateAccountBalance: updateAccountBalance.execute,
        accountBalanceLoading: updateAccountBalance.loading,
        updateInvestorToken: updateInvestorToken.execute,
        loading: fetchAccount.loading,
        initiallyLoaded,
        refresh: fetchAccount.execute,
      }}
    >
      {children}
    </InvestorAccountsContext.Provider>
  );
};
